import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { resignationAPI } from '../../services';
import { 
  FileText, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Calendar,
  User,
  MessageSquare,
  Download,
  Eye
} from 'lucide-react';

const ResignationManagement = () => {
  const { user } = useAuth();
  const [resignations, setResignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedResignation, setSelectedResignation] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [reportees, setReportees] = useState([]);
  const [availableManagers, setAvailableManagers] = useState([]);

  useEffect(() => {
    fetchResignations();
  }, [filter]);

  const fetchResignations = async () => {
    try {
      setLoading(true);
      const response = await resignationAPI.getAllResignations({ status: filter });
      setResignations(response.data.resignations);
    } catch (error) {
      console.error('Error fetching resignations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status, reporterActionStatus = null) => {
    // Special case: pending status with no reporter assigned - show as pending admin
    if (status === 'pending' && reporterActionStatus === 'not_applicable') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <AlertCircle className="w-3 h-3 mr-1" />
          Pending Admin (No Manager)
        </span>
      );
    }

    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending Manager' },
      approved_by_reporter: { color: 'bg-blue-100 text-blue-800', icon: AlertCircle, label: 'Pending Admin' },
      rejected_by_reporter: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Rejected by Manager' },
      approved_by_admin: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Approved' },
      rejected_by_admin: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Rejected by Admin' },
      withdrawn: { color: 'bg-gray-100 text-gray-800', icon: XCircle, label: 'Withdrawn' },
      completed: { color: 'bg-purple-100 text-purple-800', icon: CheckCircle, label: 'Completed' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const handleViewDetails = async (resignationId) => {
    try {
      const response = await resignationAPI.getResignationDetails(resignationId);
      setSelectedResignation(response.data.resignation);
      setReportees(response.data.reportees || []);
      setAvailableManagers(response.data.availableManagers || []);
      setShowDetails(true);
    } catch (error) {
      console.error('Error fetching resignation details:', error);
    }
  };

  const handleAdminAction = async (resignationId, action, data) => {
    try {
      await resignationAPI.adminAction(resignationId, {
        action,
        ...data
      });
      alert(`Resignation ${action} successfully`);
      setShowDetails(false);
      fetchResignations();
    } catch (error) {
      console.error('Error processing resignation:', error);
      alert(error.response?.data?.message || 'Failed to process resignation');
    }
  };

  const handleUpdateChecklist = async (resignationId, section, data) => {
    try {
      await resignationAPI.updateExitChecklist(resignationId, {
        section,
        data
      });
      alert('Checklist updated successfully');
      handleViewDetails(resignationId);
    } catch (error) {
      console.error('Error updating checklist:', error);
      alert('Failed to update checklist');
    }
  };

  const handleCompleteResignation = async (resignationId, data) => {
    try {
      await resignationAPI.completeResignation(resignationId, data);
      alert('Resignation process completed successfully');
      setShowDetails(false);
      fetchResignations();
    } catch (error) {
      console.error('Error completing resignation:', error);
      alert('Failed to complete resignation');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Resignation Management</h1>
        <p className="text-gray-600">Manage employee resignation requests and exit process</p>
      </div>

      {/* Filters */}
      <div className="mb-6 flex gap-2 flex-wrap">
        {['all', 'pending', 'approved_by_reporter', 'approved_by_admin', 'rejected_by_reporter', 'rejected_by_admin', 'completed'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === status
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            {status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </button>
        ))}
      </div>

      {/* Resignations List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Employee
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Submitted Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Working Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {resignations.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p>No resignations found</p>
                </td>
              </tr>
            ) : (
              resignations.map((resignation) => (
                <tr key={resignation._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {resignation.employee.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {resignation.employee.employeeId} • {resignation.employee.designation}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 capitalize">
                      {resignation.resignationType.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(resignation.submittedDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(resignation.lastWorkingDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(resignation.status, resignation.reporterAction?.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleViewDetails(resignation._id)}
                      className="text-blue-600 hover:text-blue-900 flex items-center"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View Details
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Details Modal */}
      {showDetails && selectedResignation && (
        <ResignationDetailsModal
          resignation={selectedResignation}
          reportees={reportees}
          availableManagers={availableManagers}
          onClose={() => setShowDetails(false)}
          onAdminAction={handleAdminAction}
          onUpdateChecklist={handleUpdateChecklist}
          onComplete={handleCompleteResignation}
        />
      )}
    </div>
  );
};

const ResignationDetailsModal = ({ resignation, reportees = [], availableManagers = [], onClose, onAdminAction, onUpdateChecklist, onComplete }) => {
  const [actionType, setActionType] = useState('');
  const [comments, setComments] = useState('');
  const [finalLWD, setFinalLWD] = useState('');
  const [newReportingTo, setNewReportingTo] = useState('');
  const [exitInterviewDate, setExitInterviewDate] = useState('');
  const [activeTab, setActiveTab] = useState('details');

  const handleSubmitAction = () => {
    if (!actionType) {
      alert('Please select an action');
      return;
    }

    // If approving and there are reportees, newReportingTo is required
    if (actionType === 'approved' && reportees.length > 0 && !newReportingTo) {
      alert('Please select a new reporting manager for the employees who report to this person');
      return;
    }

    onAdminAction(resignation._id, actionType, {
      comments,
      finalLastWorkingDate: finalLWD || resignation.lastWorkingDate,
      exitInterviewScheduled: !!exitInterviewDate,
      exitInterviewDate,
      newReportingTo: reportees.length > 0 ? newReportingTo : undefined
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Resignation Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px px-6">
            {['details', 'timeline', 'checklist'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-6 text-sm font-medium border-b-2 ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* Employee Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-3">Employee Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="font-medium">{resignation.employee.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Employee ID</p>
                    <p className="font-medium">{resignation.employee.employeeId}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Designation</p>
                    <p className="font-medium">{resignation.employee.designation}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Department</p>
                    <p className="font-medium">{resignation.employee.department}</p>
                  </div>
                </div>
              </div>

              {/* Resignation Info */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Resignation Information</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Type</p>
                    <p className="font-medium capitalize">{resignation.resignationType.replace(/_/g, ' ')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Reason</p>
                    <p className="font-medium">{resignation.reason}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Submitted Date</p>
                      <p className="font-medium">{new Date(resignation.submittedDate).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Requested Last Working Date</p>
                      <p className="font-medium">{new Date(resignation.lastWorkingDate).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Notice Period</p>
                    <p className="font-medium">{resignation.noticePeriodDays} days</p>
                  </div>
                </div>
              </div>

              {/* Reporter Action */}
              {resignation.reporterAction.status !== 'not_applicable' && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3">Reporting Manager Action</h3>
                  <div className="space-y-2">
                    <p className="text-sm">
                      <span className="text-gray-500">Status:</span>{' '}
                      <span className="font-medium capitalize">
                        {resignation.reporterAction.status === 'not_applicable' 
                          ? 'No Reporting Manager Assigned' 
                          : resignation.reporterAction.status.replace(/_/g, ' ')}
                      </span>
                    </p>
                    {resignation.reporterAction.actionBy && (
                      <>
                        <p className="text-sm">
                          <span className="text-gray-500">Actioned By:</span>{' '}
                          <span className="font-medium">{resignation.reporterAction.actionBy.name}</span>
                        </p>
                        <p className="text-sm">
                          <span className="text-gray-500">Date:</span>{' '}
                          <span className="font-medium">
                            {new Date(resignation.reporterAction.actionDate).toLocaleDateString()}
                          </span>
                        </p>
                        {resignation.reporterAction.comments && (
                          <p className="text-sm">
                            <span className="text-gray-500">Comments:</span>{' '}
                            <span className="font-medium">{resignation.reporterAction.comments}</span>
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Admin Action Form - Show when reporter approved OR when no reporter (not_applicable) */}
              {resignation.adminAction.status === 'pending' && 
               (resignation.status === 'approved_by_reporter' || 
                (resignation.status === 'pending' && resignation.reporterAction.status === 'not_applicable')) && (
                <div className="border-t pt-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Admin Action</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Action</label>
                      <select
                        value={actionType}
                        onChange={(e) => setActionType(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Action</option>
                        <option value="approved">Approve</option>
                        <option value="rejected">Reject</option>
                      </select>
                    </div>

                    {actionType === 'approved' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Final Last Working Date
                          </label>
                          <input
                            type="date"
                            value={finalLWD}
                            onChange={(e) => setFinalLWD(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Exit Interview Date (Optional)
                          </label>
                          <input
                            type="date"
                            value={exitInterviewDate}
                            onChange={(e) => setExitInterviewDate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        {/* Reportees Reassignment Section */}
                        {reportees.length > 0 && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <h4 className="font-medium text-yellow-800 mb-2 flex items-center">
                              <AlertCircle className="w-4 h-4 mr-2" />
                              Reportees Reassignment Required
                            </h4>
                            <p className="text-sm text-yellow-700 mb-3">
                              The following {reportees.length} employee(s) report to {resignation.employee.name}. 
                              Please select a new reporting manager for them:
                            </p>
                            <div className="mb-3 max-h-32 overflow-y-auto bg-white rounded border border-yellow-200 p-2">
                              {reportees.map((emp) => (
                                <div key={emp._id} className="text-sm py-1 border-b last:border-b-0 border-yellow-100">
                                  <span className="font-medium">{emp.name}</span>
                                  <span className="text-gray-500 ml-2">({emp.employeeId})</span>
                                  {emp.designation && <span className="text-gray-400 ml-2">• {emp.designation}</span>}
                                </div>
                              ))}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-yellow-800 mb-2">
                                New Reporting Manager <span className="text-red-500">*</span>
                              </label>
                              <select
                                value={newReportingTo}
                                onChange={(e) => setNewReportingTo(e.target.value)}
                                className="w-full px-3 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 bg-white"
                                required
                              >
                                <option value="">Select New Manager</option>
                                {availableManagers.map((mgr) => (
                                  <option key={mgr._id} value={mgr._id}>
                                    {mgr.name} ({mgr.employeeId}) - {mgr.designation || 'Manager'}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Comments</label>
                      <textarea
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Add your comments..."
                      />
                    </div>

                    <button
                      onClick={handleSubmitAction}
                      className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Submit Action
                    </button>
                  </div>
                </div>
              )}

              {/* Show Final Dates (Read-only after approval) */}
              {resignation.adminAction.status === 'approved' && (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h3 className="font-semibold text-gray-900 mb-3">Admin Approval Details</h3>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Final Last Working Date</p>
                        <p className="font-medium text-green-700">
                          {resignation.finalLastWorkingDate 
                            ? new Date(resignation.finalLastWorkingDate).toLocaleDateString()
                            : new Date(resignation.lastWorkingDate).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">⚠️ Cannot be changed after approval</p>
                      </div>
                      {resignation.exitInterviewScheduled && resignation.exitInterviewDate && (
                        <div>
                          <p className="text-sm text-gray-500">Exit Interview Date</p>
                          <p className="font-medium text-green-700">
                            {new Date(resignation.exitInterviewDate).toLocaleDateString()}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">⚠️ Cannot be changed after approval</p>
                        </div>
                      )}
                    </div>
                    {resignation.adminAction.actionBy && (
                      <>
                        <div>
                          <p className="text-sm text-gray-500">Approved By</p>
                          <p className="font-medium">{resignation.adminAction.actionBy.name}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Approval Date</p>
                          <p className="font-medium">
                            {new Date(resignation.adminAction.actionDate).toLocaleDateString()}
                          </p>
                        </div>
                        {resignation.adminAction.comments && (
                          <div>
                            <p className="text-sm text-gray-500">Comments</p>
                            <p className="font-medium">{resignation.adminAction.comments}</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Show Rejection Details */}
              {resignation.adminAction.status === 'rejected' && (
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <h3 className="font-semibold text-gray-900 mb-3">Admin Rejection Details</h3>
                  <div className="space-y-2">
                    {resignation.adminAction.actionBy && (
                      <>
                        <p className="text-sm">
                          <span className="text-gray-500">Rejected By:</span>{' '}
                          <span className="font-medium">{resignation.adminAction.actionBy.name}</span>
                        </p>
                        <p className="text-sm">
                          <span className="text-gray-500">Rejection Date:</span>{' '}
                          <span className="font-medium">
                            {new Date(resignation.adminAction.actionDate).toLocaleDateString()}
                          </span>
                        </p>
                        {resignation.adminAction.comments && (
                          <p className="text-sm">
                            <span className="text-gray-500">Comments:</span>{' '}
                            <span className="font-medium">{resignation.adminAction.comments}</span>
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'timeline' && (
            <div className="space-y-4">
              {resignation.timeline.map((entry, index) => (
                <div key={index} className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Clock className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{entry.action}</p>
                    <p className="text-sm text-gray-500">
                      {entry.performedBy?.name} • {new Date(entry.timestamp).toLocaleString()}
                    </p>
                    {entry.comments && <p className="text-sm text-gray-600 mt-1">{entry.comments}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'checklist' && (
            <div className="space-y-6">
              <p className="text-gray-600">Exit process checklist and clearance tracking</p>
              {/* Add checklist UI here */}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResignationManagement;
